/// <reference types="vite/client" />
import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '../api/client/config';
import { invalidateResearch } from '../features/company-research/companyResearchCache';

let socket: Socket | null = null;

export function getSocket(): Socket | null {
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

export function useSocket() {
  const { accessToken, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    // Create socket connection
    socket = io(API_BASE_URL, {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Socket connected:', socket?.id);
    });

    socket.on('disconnect', (reason) => {
      console.warn('Socket disconnected:', reason);
    });

    socket.on('notification:new', (notification) => {
      addNotification({
        id: notification.id ?? notification._id ?? `notification-${Date.now()}`,
        type: notification.type ?? 'system',
        title: notification.title ?? 'New notification',
        body: notification.body ?? notification.message ?? '',
        read: notification.read ?? false,
        actionUrl: notification.actionUrl,
        createdAt: notification.createdAt ?? new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ['notifications-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    socket.on('agent:status_change', (data) => {
      console.log('Agent status change:', data);
    });

    socket.on('message:new', (message) => {
      console.log('New message:', message);
    });

    socket.on('sync:complete', (data) => {
      console.log('Sync complete:', data.integration);
    });

    // ── Company Research real-time events ──────────────────────────────────
    socket.on('research:progress', (data: { recordId: string; stage: string; message: string }) => {
      // Update the cached record's progressMessage so the card can show it
      queryClient.setQueryData(['company-research'], (old: any) => {
        if (!Array.isArray(old?.data)) return old;
        return {
          ...old,
          data: old.data.map((r: any) =>
              r._id === data.recordId ? { ...r, _progressMessage: data.message } : r
            ),
        };
      });
    });

    socket.on('research:completed', (data: { recordId: string }) => {
      // Immediately refetch so the card flips to completed without waiting for poll
      invalidateResearch(queryClient, data.recordId);
      console.log('Research completed:', data.recordId);
    });

    socket.on('research:failed', (data: { recordId: string; error: string }) => {
      invalidateResearch(queryClient, data.recordId);
      console.warn('Research failed:', data.recordId, data.error);
    });

    return () => {
      disconnectSocket();
      socketRef.current = null;
    };
  }, [isAuthenticated, accessToken, queryClient]);

  return socketRef.current;
}
